// Landing pages · lista con carpetas, búsqueda, duplicar, eliminar, crear e importar de Clientify.
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Download, ExternalLink, Folder, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDate } from "../../../lib/format.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Modal, TableSkeleton } from "../../../components/ui.tsx";
import { SearchInput } from "../../../components/SearchInput.tsx";
import { toast } from "../../../components/toast.tsx";
import { type LandingListItem, Thumb, pct } from "./shared.tsx";

export function LandingList(): JSX.Element {
  const me = useMe();
  const admin = Boolean(me.data?.admin);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState<string>("");
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("https://tepuedeinteresar.com/");

  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (folder) params.set("folder", folder);
  const list = useQuery<{ rows: LandingListItem[]; folders: string[] }>({
    queryKey: ["mkt-landings", q, folder],
    queryFn: () => api.get(`/mkt/landings${params.toString() ? `?${params}` : ""}`),
  });
  const inval = (): void => { void qc.invalidateQueries({ queryKey: ["mkt-landings"] }); };
  const onErr = (e: unknown): void => toast.error(e instanceof ApiError ? e.message : "No se pudo completar la acción.");

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>("/mkt/landings", { name: name.trim(), ...(folder ? { folder } : {}) }),
    onSuccess: (r) => { inval(); nav(`${r.id}/editor`); }, onError: onErr,
  });
  const imp = useMutation({
    mutationFn: () => api.post<{ id: string; images: number; imagesFailed: number; forms: number }>("/mkt/landings/import-url", { url: url.trim(), name: name.trim() }),
    onSuccess: (r) => {
      toast.success(`Landing importada como borrador: ${r.images} imágenes copiadas${r.imagesFailed ? ` (${r.imagesFailed} no se pudieron)` : ""}, ${r.forms} formulario(s) por reemplazar.`);
      inval(); nav(r.id);
    }, onError: onErr,
  });
  const dup = useMutation({
    mutationFn: (id: string) => api.post(`/mkt/landings/${id}/duplicate`),
    onSuccess: () => { toast.success("Landing duplicada (borrador)."); inval(); }, onError: onErr,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/mkt/landings/${id}`),
    onSuccess: () => { toast.success("Landing eliminada."); inval(); }, onError: onErr,
  });

  const rows = list.data?.rows ?? [];
  const folders = list.data?.folders ?? [];
  const close = (): void => { setCreating(false); setImporting(false); setName(""); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <h2 className="text-lg font-semibold text-ink">Landing pages</h2>
        <div className="flex flex-wrap gap-2">
          <SearchInput value={q} onValueChange={setQ} placeholder="Buscar landing…" className="w-56" />
          {admin && <Button onClick={() => { setImporting(true); setCreating(false); }}><Download size={16} strokeWidth={1.75} /> Importar de Clientify</Button>}
          {admin && <Button variant="primary" onClick={() => { setCreating(true); setImporting(false); }}><Plus size={16} strokeWidth={1.75} /> Nueva landing</Button>}
        </div>
      </div>

      <Modal open={creating} onClose={close} title="Nueva landing page"
        footer={<><Button onClick={close}>Cancelar</Button><Button variant="primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>Crear y editar</Button></>}>
        <Field label="Nombre" required><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={160} autoFocus placeholder="Ej.: Summit 2026" /></Field>
      </Modal>
      <Modal open={importing} onClose={close} title="Importar landing publicada en Clientify"
        footer={<><Button onClick={close}>Cancelar</Button><Button variant="primary" disabled={!name.trim() || !url.trim() || imp.isPending} onClick={() => imp.mutate()}>{imp.isPending ? "Importando…" : "Importar"}</Button></>}>
        <div className="space-y-3">
          <Field label="URL pública" required hint="Solo tepuedeinteresar.com o dominios de Clientify. Las imágenes se copian a la biblioteca de medios.">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} maxLength={500} placeholder="https://tepuedeinteresar.com/mi-landing" />
          </Field>
          <Field label="Nombre" required><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={160} /></Field>
          <p className="text-xs text-ink-muted">Se guarda como borrador. Los formularios de Clientify se desactivan: asigna un formulario de Tiger en la pestaña Publicación.</p>
        </div>
      </Modal>

      <div className="flex flex-col md:flex-row gap-4">
        {folders.length > 0 && (
          <Card className="p-2 md:w-52 shrink-0 h-fit">
            <div className="dt-label px-2 py-1">Carpetas</div>
            {["", ...folders].map((f) => (
              <button key={f || "_all"} onClick={() => setFolder(f)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left ${folder === f ? "bg-primary-soft text-primary font-medium" : "text-ink-secondary hover:bg-surface-hover"}`}>
                <Folder size={14} strokeWidth={1.75} /> <span className="truncate">{f || "Todas"}</span>
              </button>
            ))}
          </Card>
        )}
        <Card className="flex-1 min-w-0 overflow-hidden">
          {list.isLoading ? <TableSkeleton rows={5} cols={6} />
            : list.isError ? <ErrorState message="No se pudieron cargar las landings." onRetry={() => void list.refetch()} />
            : !rows.length ? <EmptyState title="Sin landing pages" body="Crea una landing con el editor por bloques o importa una de Clientify." />
            : (
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full">
                  <thead><tr>
                    <th className="th">Landing</th><th className="th">Creada por</th><th className="th">Estado</th><th className="th">Fecha</th>
                    <th className="th text-right">Visitas</th><th className="th text-right">Conversiones</th><th className="th text-right">% Conv.</th><th className="th" />
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-surface-hover cursor-pointer" onClick={() => nav(r.id)}>
                        <td className="td">
                          <div className="flex items-center gap-3 min-w-[220px]">
                            <Thumb src={r.thumb} />
                            <div className="min-w-0">
                              <div className="font-medium text-ink truncate">{r.name}</div>
                              <div className="text-xs text-ink-muted truncate">/{r.slug}{r.source === "clientify" ? " · importada" : ""}</div>
                            </div>
                          </div>
                        </td>
                        <td className="td whitespace-nowrap">{r.createdByName ?? "—"}</td>
                        <td className="td"><Badge color={r.status === "published" ? "green" : "gray"}>{r.status === "published" ? "Publicada" : "Borrador"}</Badge></td>
                        <td className="td whitespace-nowrap">{formatDate(r.createdAt)}</td>
                        <td className="td text-right tnum">{r.visits}</td>
                        <td className="td text-right tnum">{r.conversions}</td>
                        <td className="td text-right tnum">{pct(r.conversions, r.uniqueVisitors)}</td>
                        <td className="td" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {r.status === "published" && <a className="btn-ghost btn-sm" href={r.publicUrl} target="_blank" rel="noopener noreferrer" title="Ver"><ExternalLink size={14} /></a>}
                            {admin && <button className="btn-ghost btn-sm" title="Duplicar" onClick={() => dup.mutate(r.id)}><Copy size={14} /></button>}
                            {admin && <button className="btn-ghost btn-sm text-danger" title="Eliminar"
                              onClick={() => { if (window.confirm(`¿Eliminar la landing «${r.name}»? Se perderán sus estadísticas.`)) del.mutate(r.id); }}><Trash2 size={14} /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </Card>
      </div>
    </div>
  );
}
